import { useState, useCallback } from 'react'
import type {
  LearningConfigurationV2,
  BranchableLevelConfig,
  ComponentConfigV2,
  HierarchyLevel,
} from '@/types/configurationV2'
import { createDefaultLearningConfigurationV2 } from '@/types/configurationV2'
import {
  QuizOptionsEditor,
  VideoOptionsEditor,
  ToonOptionsEditor,
  AssignmentOptionsEditor,
  InteractiveOptionsEditor,
  GuideOptionsEditor,
  BuildupOptionsEditor,
  WarmupOptionsEditor,
} from './editors'
import LevelTab from './LevelTab'
import LessonTab from './LessonTab'
import CompletionRulesV2Editor from './CompletionRulesV2Editor'
import LearningSettingsV2Editor from './LearningSettingsV2Editor'

type TabId = 'product' | 'curriculum' | 'course' | 'module' | 'lesson' | 'completionRules' | 'learningSettings'

const LEVEL_TABS: { id: TabId; label: string }[] = [
  { id: 'product', label: 'Product' },
  { id: 'curriculum', label: 'Curriculum' },
  { id: 'course', label: 'Course' },
  { id: 'module', label: 'Module' },
  { id: 'lesson', label: 'Lesson' },
]

const COMMON_TABS: { id: TabId; label: string }[] = [
  { id: 'completionRules', label: '완료조건' },
  { id: 'learningSettings', label: '학습설정' },
]

const LEVEL_BRANCH_MAP: Record<string, HierarchyLevel[]> = {
  product: [],
  curriculum: ['product'],
  course: ['product', 'curriculum'],
  module: ['product', 'curriculum', 'course'],
}

interface ConfigurationBuilderV2Props {
  initialConfiguration?: LearningConfigurationV2
  onChange: (config: LearningConfigurationV2) => void
  onCancel?: () => void
}

export default function ConfigurationBuilderV2({
  initialConfiguration,
  onChange,
  onCancel,
}: ConfigurationBuilderV2Props) {
  const [config, setConfig] = useState<LearningConfigurationV2>(
    initialConfiguration ?? createDefaultLearningConfigurationV2()
  )
  const [activeTab, setActiveTab] = useState<TabId>('lesson')

  const updateConfig = useCallback(
    (updater: (prev: LearningConfigurationV2) => LearningConfigurationV2) => {
      setConfig((prev) => {
        const next = updater(prev)
        onChange(next)
        return next
      })
    },
    [onChange]
  )

  const renderOptionsEditor = useCallback(
    (component: ComponentConfigV2, onUpdate: (u: ComponentConfigV2) => void) => {
      switch (component.type) {
        case 'quiz': return <QuizOptionsEditor component={component} onUpdate={onUpdate} />
        case 'video': return <VideoOptionsEditor component={component} onUpdate={onUpdate} />
        case 'toon': return <ToonOptionsEditor component={component} onUpdate={onUpdate} />
        case 'assignment': return <AssignmentOptionsEditor component={component} onUpdate={onUpdate} />
        case 'interactive': return <InteractiveOptionsEditor component={component} onUpdate={onUpdate} />
        case 'guide': return <GuideOptionsEditor component={component} onUpdate={onUpdate} />
        case 'buildup': return <BuildupOptionsEditor component={component} onUpdate={onUpdate} />
        case 'warmup': return <WarmupOptionsEditor component={component} onUpdate={onUpdate} />
        default: return null
      }
    },
    []
  )

  const isLevelTab = (tab: TabId): tab is HierarchyLevel =>
    ['product', 'curriculum', 'course', 'module'].includes(tab)

  return (
    <div className="space-y-4">
      {/* Tab Bar */}
      <div className="flex gap-0.5 border-b-2 border-purple-600">
        {LEVEL_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : 'bg-purple-300 text-white hover:bg-purple-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
        {COMMON_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-yellow-400 text-gray-900 font-bold'
                : 'bg-yellow-300 text-gray-700 hover:bg-yellow-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="border-2 border-purple-600 border-t-0 rounded-b-xl p-5 bg-gray-50 min-h-[400px]">
        {isLevelTab(activeTab) && (
          <LevelTab
            level={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            config={config[activeTab] as BranchableLevelConfig}
            onChange={(levelConfig) =>
              updateConfig((prev) => ({ ...prev, [activeTab]: levelConfig }))
            }
            allowedBranchLevels={LEVEL_BRANCH_MAP[activeTab]}
            renderOptionsEditor={renderOptionsEditor}
          />
        )}

        {activeTab === 'lesson' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              핵심 체크와 빌드업 교사 채점은 템플릿 옵션이 아니라 레슨의 깨처 콘텐츠 데이터로 결정됩니다.
              자동완료 실행 전 미리보기에서 항목별 필수 개수를 반드시 확인하세요.
            </div>
            <LessonTab
              config={config.lesson}
              onChange={(lessonConfig) =>
                updateConfig((prev) => ({ ...prev, lesson: lessonConfig }))
              }
              renderOptionsEditor={renderOptionsEditor}
            />
          </div>
        )}

        {activeTab === 'completionRules' && (
          <CompletionRulesV2Editor
            rules={config.completionRules}
            onChange={(rules) =>
              updateConfig((prev) => ({ ...prev, completionRules: rules }))
            }
          />
        )}

        {activeTab === 'learningSettings' && (
          <LearningSettingsV2Editor
            settings={config.learningSettings}
            onChange={(settings) =>
              updateConfig((prev) => ({ ...prev, learningSettings: settings }))
            }
          />
        )}
      </div>

      {/* Actions */}
      {onCancel && (
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            취소
          </button>
          <button
            onClick={() => onChange(config)}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            저장
          </button>
        </div>
      )}
    </div>
  )
}
